import React, { useMemo } from "react";
import { Grid } from "@material-ui/core";

import { AssetTypeNameOther } from "../../../types/search/asset/asset-type-results/iasset-type-results.interface";
import { END_DATE, START_DATE } from "../../../types/templates/brief-fields.constants";
import { TemplateGrid } from "../../../components/template-grid/template-grid.component";
import { TemplateName } from "../../../types/templates/template-name";
import { useWorkOrderTemplateOptions } from "../../../pages/work-order/types/work-order-template.hooks";
import { WORK_ORDER_HOOK } from "../../../pages/work-order/types/work-order-template.interface";
import { WORK_ORDER_TEMPLATE } from "../../../types/templates/work-order/work-order-template.enum";

import { BorderedBlock } from "../../common-attributes/components/bordered-block/bordered-block.component";
import { CheckboxField } from "../../forms/fields/checkbox-field/checkbox-field.component";
import { DateField } from "../../forms/fields/date-field/date-field.component";
import { RadioGroup } from "../../forms/fields/radio-group/radio-group.component";
import { RichTextField } from "../../forms/fields/rich-text-field/components/rich-text-field/rich-text-field.component";
import { SelectField } from "../../forms/fields/select-field/select-field.component";
import { StatusSection } from "../../common-attributes/components/status-section/status-section.component";
import { TextField } from "../../forms/fields/text-field/text-field.component";
import { UserSearchField } from "../../forms/fields/user-search-field/user-search-field.component";

import {
  ASSET_DESCRIPTION,
  ASSET_DIMENSION,
  ASSET_TYPE,
  CONTENT_DIRECTION,
  DUE_AT,
  FILE_FORMAT_DESCRIPTION,
  IN_WAREHOUSE_DATE,
  IS_BUY_GET,
  IS_CREATIVE_REVIEW_REQUIRED,
  IS_EN_REQUIRED,
  IS_FR_REQUIRED,
  NUM_OF_STORES_EN,
  NUM_OF_STORES_FR,
  OFFER_DETAILS,
  POP_STORE_TYPE,
  PORTAL_OR_PRINT,
  QTY_PER_STORE_EN,
  QTY_PER_STORE_FR,
  SelectionsForPortalOrPrint,
} from "../constants";

import { UserSearchFieldSelection } from "./common/user-search-field-selection/user-search-field-selection.component";
import { IWorkOrderTemplate, pageType } from ".";
import { HiddenHeading } from "../../../pages/pages.styles";

export const WorkOrderInStorePrintTemplate = (props: IWorkOrderTemplate) => {
  const {
    assetTypeList,
    assetTypeOptions,
    fileFormatOptions,
    storeTypeOptions,
  } = useWorkOrderTemplateOptions(
    [WORK_ORDER_HOOK.ASSET_TYPES, WORK_ORDER_HOOK.FILE_FORMATS, WORK_ORDER_HOOK.STORE_TYPES],
    WORK_ORDER_TEMPLATE.INSTORE_PRINT
  );

  const selectedAssetType = props.form.watch(ASSET_TYPE.ID);
  const showAssetTypeDescription = useMemo<boolean>(() => {
    const selectionLabel = assetTypeOptions.find(
      (assetType) => assetType.value === Number(selectedAssetType)
    )?.label;

    return selectionLabel?.toLowerCase() === AssetTypeNameOther;
  }, [assetTypeOptions, selectedAssetType]);

  const getAssetTypeDimension = (assetTypeId: number) => {
    const assetType = assetTypeList.find((type) => type.id === assetTypeId);

    return assetType?.assetDimensions[0]?.assetDimensions ?? "";
  };

  const handleAssetTypeChange = (event: React.ChangeEvent<any>) => {
    if (event.target.value === AssetTypeNameOther) {
      props.form.setValue(ASSET_DIMENSION.ID, "");
    } else {
      props.form.setValue(ASSET_DESCRIPTION.ID, "");
      props.form.setValue(ASSET_DIMENSION.ID, getAssetTypeDimension(event.target.value));
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <HiddenHeading>{props.pageTitle}</HiddenHeading>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={CONTENT_DIRECTION.TITLE}
            name={CONTENT_DIRECTION.ID}
            required
            subTitle={TemplateName.WorkOrder.InStorePrint}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DateField
                form={props.form}
                label={START_DATE.TITLE}
                name={START_DATE.ID}
                required
                userPermissions={props.userPermissions}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateField
                form={props.form}
                label={END_DATE.TITLE}
                name={END_DATE.ID}
                required
                userPermissions={props.userPermissions}
              />
            </Grid>
          </Grid>
        </BorderedBlock>
        <TemplateGrid container spacing={3}>
          <Grid item xs={12} md={6}>
            <BorderedBlock>
              <CheckboxField
                form={props.form}
                label={IS_EN_REQUIRED.TITLE}
                name={IS_EN_REQUIRED.ID}
                userPermissions={props.userPermissions}
              >
                <TextField
                  form={props.form}
                  label={QTY_PER_STORE_EN.TITLE}
                  name={QTY_PER_STORE_EN.ID}
                  numeric
                  userPermissions={props.userPermissions}
                />
                <TextField
                  form={props.form}
                  label={NUM_OF_STORES_EN.TITLE}
                  name={NUM_OF_STORES_EN.ID}
                  numeric
                  userPermissions={props.userPermissions}
                />
              </CheckboxField>
            </BorderedBlock>
          </Grid>
          <Grid item xs={12} md={6}>
            <BorderedBlock>
              <CheckboxField
                form={props.form}
                label={IS_FR_REQUIRED.TITLE}
                name={IS_FR_REQUIRED.ID}
                userPermissions={props.userPermissions}
              >
                <TextField
                  form={props.form}
                  label={QTY_PER_STORE_FR.TITLE}
                  name={QTY_PER_STORE_FR.ID}
                  numeric
                  userPermissions={props.userPermissions}
                />
                <TextField
                  form={props.form}
                  label={NUM_OF_STORES_FR.TITLE}
                  name={NUM_OF_STORES_FR.ID}
                  numeric
                  userPermissions={props.userPermissions}
                />
              </CheckboxField>
            </BorderedBlock>
          </Grid>
        </TemplateGrid>
        <BorderedBlock>
          <Grid container spacing={3}>
            <Grid item xs={8} md={6}>
              <SelectField
                form={props.form}
                label={ASSET_TYPE.TITLE}
                name={ASSET_TYPE.ID}
                onChange={handleAssetTypeChange}
                options={assetTypeOptions}
                required
                userPermissions={props.userPermissions}
              />
              <TextField
                showIf={showAssetTypeDescription}
                form={props.form}
                label={ASSET_DESCRIPTION.TITLE}
                name={ASSET_DESCRIPTION.ID}
                userPermissions={props.userPermissions}
              />
            </Grid>
            <Grid item xs={8} md={6}>
              <TextField
                form={props.form}
                label={ASSET_DIMENSION.TITLE}
                name={ASSET_DIMENSION.ID}
                userPermissions={props.userPermissions}
              />
            </Grid>
          </Grid>
        </BorderedBlock>
        <BorderedBlock>
          <RichTextField
            form={props.form}
            label={OFFER_DETAILS.TITLE}
            name={OFFER_DETAILS.ID}
            userPermissions={props.userPermissions}
            withAdd
          />
        </BorderedBlock>
        <BorderedBlock>
          <SelectField
            form={props.form}
            label={POP_STORE_TYPE.TITLE}
            multiple
            name={POP_STORE_TYPE.ID}
            options={storeTypeOptions}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
      </Grid>
      <Grid item xs={12} md={4}>
        <StatusSection
          form={props.form}
          label="Status"
          name="status"
          pageType={pageType}
          userPermissions={props.userPermissions}
        />
        <BorderedBlock>
          <UserSearchField
            form={props.form}
            label="Assign"
            name="contentCreatorsSelection"
            resourceId={props.resourceId}
            resourceType={props.resourceType}
            SelectionComponent={UserSearchFieldSelection}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <DateField
            form={props.form}
            label={DUE_AT.TITLE}
            name={DUE_AT.ID}
            required
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <DateField
            form={props.form}
            label={IN_WAREHOUSE_DATE.TITLE}
            name={IN_WAREHOUSE_DATE.ID}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <CheckboxField
            form={props.form}
            label={IS_CREATIVE_REVIEW_REQUIRED.TITLE}
            name={IS_CREATIVE_REVIEW_REQUIRED.ID}
            userPermissions={props.userPermissions}
          />
          <CheckboxField
            form={props.form}
            label={IS_BUY_GET.TITLE}
            name={IS_BUY_GET.ID}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
        <BorderedBlock>
          <SelectField
            form={props.form}
            label="File Format"
            name="fileFormat"
            options={fileFormatOptions}
            userPermissions={props.userPermissions}
          />
          <RichTextField
            form={props.form}
            label={FILE_FORMAT_DESCRIPTION.TITLE}
            name={FILE_FORMAT_DESCRIPTION.ID}
            userPermissions={props.userPermissions}
            withAdd
          />
        </BorderedBlock>
        <BorderedBlock>
          <RadioGroup
            form={props.form}
            label={PORTAL_OR_PRINT.TITLE}
            name={PORTAL_OR_PRINT.ID}
            options={SelectionsForPortalOrPrint}
            userPermissions={props.userPermissions}
          />
        </BorderedBlock>
      </Grid>
    </Grid>
  );
};
